import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, UserCircle, Menu, X } from 'lucide-react';
import AdminSidebar from './AdminSidebar';

export default function AdminLayout({ children, activeUser, mobileMenuOpen, setMobileMenuOpen }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState(location.state?.activeSection || 'users');

    // Animation state for mobile menu
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuAnimating, setMenuAnimating] = useState(false);

    useEffect(() => {
        if (mobileMenuOpen) {
            setMenuVisible(true);
            // Allow DOM to paint before triggering animation class
            requestAnimationFrame(() => setMenuAnimating(true));
        } else {
            setMenuAnimating(false);
            // Wait for close animation to finish before removing from DOM
            const t = setTimeout(() => setMenuVisible(false), 280);
            return () => clearTimeout(t);
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (location.pathname === '/admin' && location.state?.activeSection) {
            setActiveSection(location.state.activeSection);
            // Replace state so refresh doesn't hold old state
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem('adminSidebarCollapsed') === 'true';
        } catch { return false; }
    });

    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem('adminSidebarCollapsed', String(next)); } catch { }
            return next;
        });
    };

    return (
        <div className="flex flex-1 overflow-hidden relative w-full h-full">
            {/* Desktop Sidebar */}
            <div
                className="hidden md:block shrink-0 z-20 h-full relative border-r border-gray-100 bg-white overflow-visible"
                style={{
                    width: sidebarCollapsed ? '72px' : '256px',
                    transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                <AdminSidebar
                    activeSection={activeSection}
                    onSectionChange={setActiveSection}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={toggleSidebar}
                />
            </div>

            {/* Mobile Sidebar Overlay — animated slide-down */}
            {menuVisible && (
                <div
                    className="md:hidden fixed inset-0 z-30 top-16"
                    style={{
                        backgroundColor: menuAnimating ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
                        transition: 'background-color 280ms ease'
                    }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) setMobileMenuOpen(false); }}
                >
                    <div
                        className="absolute top-0 left-0 right-0 bg-white border-b shadow-lg max-h-[70vh] overflow-y-auto"
                        style={{
                            transform: menuAnimating ? 'translateY(0)' : 'translateY(-100%)',
                            opacity: menuAnimating ? 1 : 0,
                            transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 280ms ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <AdminSidebar
                            activeSection={activeSection}
                            onSectionChange={(section) => {
                                setActiveSection(section);
                                setMobileMenuOpen(false);
                            }}
                            collapsed={false}
                            onToggleCollapse={() => setMobileMenuOpen(false)}
                            isMobile={true}
                        />
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto p-3 md:p-6 pb-20 bg-slate-50 relative">

                {React.isValidElement(children) ? React.cloneElement(children, { activeSection }) : children}
            </main>
        </div>
    );
}
